import { useTranslation } from "react-i18next";
import {
  IconBook2,
  IconLoader2,
  IconLock,
  IconLockOpen,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
} from "@tabler/icons-react";
import { Button } from "../../../components/ui/Button";
import { Tag } from "../../../components/ui/Tag";
import {
  getArticleTypeText,
  getArticleTypeVariant,
} from "../../../domains/app/utils/getArticleTypeText";
import {
  renderArticleDate,
  renderDate,
} from "../../../domains/app/utils/renderDate";
import { cn } from "../../../lib/utils";
import type { ArticlePageArticle } from "../hooks/useArticleData";
import { SourceLabel } from "../../../components/ui/SourceLabel";

interface ArticlePageHeroProps {
  article: ArticlePageArticle;
  /** Play/pause narration trigger. */
  onToggleAudio: () => void;
  /** Whether this article's narration is currently playing. */
  isPlaying: boolean;
  isSynthesizing: boolean;
}

export function ArticlePageHero({
  article,
  onToggleAudio,
  isPlaying,
  isSynthesizing,
}: ArticlePageHeroProps) {
  const { t } = useTranslation();

  const firstAuthor = article.primary_author ?? article.authors.at(0);
  const totalAuthors =
    article.authors.length + (article.primary_author ? 1 : 0);
  const authorNamesText =
    firstAuthor && totalAuthors > 1
      ? `${firstAuthor} ${t("et al.")}`
      : (firstAuthor ?? "");

  const journalName = article.journal === null ? null : article.journal.name;
  const isOpenAccess = article.isOpenAccess === true;
  const AccessIcon = isOpenAccess ? IconLockOpen : IconLock;

  return (
    <div className="flex flex-col gap-1 border-b border-ds-slate-200 px-4 py-3 font-dm-sans">
      {/* Journal */}
      {journalName === null ? null : (
        <div className="flex w-full min-w-0 items-start gap-1 text-footnote">
          {/* One text-footnote line tall (h-5), like the PublicationCard tile:
              the icon then centres on the first line instead of on a 24px box
              that overhangs it, and stays put when a long name wraps. */}
          <span className="inline-flex h-5 w-6 shrink-0 items-center justify-center rounded-lg text-ds-slate-600">
            <IconBook2 stroke={1.5} className="size-4" />
          </span>
          <span className="line-clamp-2 min-w-0">
            <SourceLabel
              source={{ kind: "in", name: journalName }}
              onNameClick={null}
            />
          </span>
        </div>
      )}

      {/* Title */}
      <h1 className="m-0 w-full text-publication-lg font-bold text-primary-black">
        {article.title}
      </h1>

      {/* Author */}
      {authorNamesText === "" ? null : (
        <span className="text-caption font-normal text-ds-slate-600">
          {authorNamesText}
        </span>
      )}

      {/* Type · date · access + Listen */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-caption text-ds-slate-600">
          <Tag
            size="sm"
            variant={getArticleTypeVariant(article.article_type)}
            state="idle"
            icon={null}
            className="shrink-0"
          >
            {getArticleTypeText(article.article_type)}
          </Tag>
          <div className="flex items-center gap-1 whitespace-nowrap">
            <span className="font-normal">
              ·{" "}
              {article.publication_date === null
                ? renderDate(article.meta.creationDate)
                : renderArticleDate(article.publication_date)}{" "}
              ·
            </span>
            <span className="inline-flex size-5 items-center justify-center">
              <AccessIcon
                stroke={1.5}
                className={cn(
                  "size-4.5",
                  isOpenAccess ? "text-ds-mint-700" : "text-ds-lemon-600"
                )}
              />
            </span>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          leftIcon={
            isSynthesizing ? (
              <IconLoader2 className="animate-spin" />
            ) : isPlaying ? (
              <IconPlayerPauseFilled />
            ) : (
              <IconPlayerPlayFilled />
            )
          }
          onClick={onToggleAudio}
          disabled={isSynthesizing}
        >
          {isPlaying ? t("Pause") : t("Listen")}
        </Button>
      </div>
    </div>
  );
}
